// CT Volume Preloader Service
// Preloads all CT slice data in the background when the app starts

import ctSliceCache from './ctSliceCache';
import API_URL from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('armita_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

class CTPreloaderService {
  constructor() {
    this.API_URL = API_URL;
    this.isPreloading = false;
    this.preloadStatus = {
      axial: { loaded: false, progress: 0, total: 0 },
      sagittal: { loaded: false, progress: 0, total: 0 },
      coronal: { loaded: false, progress: 0, total: 0 }
    };
    this.onProgressCallbacks = [];
  }
  
  // Subscribe to progress updates
  onProgress(callback) {
    this.onProgressCallbacks.push(callback);
    // Return unsubscribe function
    return () => {
      this.onProgressCallbacks = this.onProgressCallbacks.filter(cb => cb !== callback);
    };
  }
  
  // Notify all subscribers of progress
  notifyProgress() {
    this.onProgressCallbacks.forEach(callback => {
      callback(this.preloadStatus);
    });
  }
  
  // Get current preload status
  getStatus() {
    return this.preloadStatus;
  }
  
  // Check if a specific view is loaded
  isViewLoaded(viewType) {
    return this.preloadStatus[viewType.toLowerCase()]?.loaded || false;
  }
  
  // Update progress for a specific view (called by CTSliceViewer)
  updateViewProgress(viewType, loadedCount) {
    const viewLower = viewType.toLowerCase();
    const status = this.preloadStatus[viewLower];
    
    if (status && status.total > 0) {
      status.progress = (loadedCount / status.total) * 100;
      
      if (loadedCount >= status.total) {
        status.loaded = true;
        status.progress = 100;
      }
      
      this.notifyProgress();
    }
  }
  
  // Initialize volume info
  async initializeVolumeInfo() {
    if (this.preloadStatus.axial.total > 0) {
      return; // Already initialized
    }
    
    try {
      const response = await fetch(`${this.API_URL}/nifti/info`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch NIFTI info');
      }
      
      const data = await response.json();
      
      // Update total slices for each view
      if (data.ct && data.ct.slices) {
        this.preloadStatus.axial.total = data.ct.slices.axial || 0;
        this.preloadStatus.sagittal.total = data.ct.slices.sagittal || 0;
        this.preloadStatus.coronal.total = data.ct.slices.coronal || 0;
        this.notifyProgress();
      }
    } catch (error) {
      console.error('Error fetching volume info:', error);
      throw error;
    }
  }
  
  // Start preloading all views in the background
  async startPreloading() {
    if (this.isPreloading) {
      console.log('CT preloading already in progress');
      return;
    }
    
    this.isPreloading = true;
    console.log('Starting background CT preloading...');
    
    try {
      // Initialize volume info if needed
      await this.initializeVolumeInfo();
      
      // Preload all views concurrently
      const preloadPromises = [
        this.preloadView('axial'),
        this.preloadView('sagittal'),
        this.preloadView('coronal')
      ];
      
      await Promise.all(preloadPromises);
      
      console.log('Background CT preloading complete!');
      this.isPreloading = false;
      
    } catch (error) {
      console.error('Error during background preloading:', error);
      this.isPreloading = false;
    }
  }
  
  // Preload a specific view
  async preloadView(viewType) {
    const viewLower = viewType.toLowerCase();
    const totalSlices = this.preloadStatus[viewLower].total;
    
    if (totalSlices === 0) {
      console.warn(`No slices to preload for ${viewType} view`);
      return;
    }
    
    // Check if already fully cached
    const cacheKey = `${viewLower}_0`;
    if (ctSliceCache.cache.has(cacheKey)) {
      // Check how many are already cached
      let cachedCount = 0;
      for (let i = 0; i < totalSlices; i++) {
        if (ctSliceCache.cache.has(`${viewLower}_${i}`)) {
          cachedCount++;
        }
      }
      
      if (cachedCount === totalSlices) {
        console.log(`${viewType} view already fully cached (${cachedCount} slices)`);
        this.preloadStatus[viewLower].loaded = true;
        this.preloadStatus[viewLower].progress = 100;
        this.notifyProgress();
        return;
      }
      
      console.log(`${viewType} view partially cached (${cachedCount}/${totalSlices} slices)`);
    }
    
    console.log(`Preloading ${totalSlices} slices for ${viewType} view...`);
    
    const chunkSize = 10; // Smaller chunks for background loading
    let loaded = 0;
    
    for (let i = 0; i < totalSlices; i += chunkSize) {
      const promises = [];
      for (let j = i; j < Math.min(i + chunkSize, totalSlices); j++) {
        // Skip if already cached
        if (!ctSliceCache.cache.has(`${viewLower}_${j}`)) {
          promises.push(ctSliceCache.fetchSlice(viewLower, j).catch(err => {
            console.warn(`Failed to preload ${viewType} slice ${j}:`, err);
          }));
        } else {
          loaded++; // Count cached slices
        }
      }
      
      if (promises.length > 0) {
        await Promise.all(promises);
        loaded += promises.length;
      }
      
      // Update progress
      this.preloadStatus[viewLower].progress = (loaded / totalSlices) * 100;
      this.notifyProgress();
      
      // Small delay to prevent blocking the UI
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    this.preloadStatus[viewLower].loaded = true;
    this.preloadStatus[viewLower].progress = 100;
    this.notifyProgress();
    
    console.log(`Completed preloading ${viewType} view`);
  }
}

// Create singleton instance
const ctPreloader = new CTPreloaderService();

export default ctPreloader;